import React from "react"
import { useTranslation } from "react-i18next"

import * as BikeApi from "features/bike/api/BikeApi"
import { useAlert, useAuth, useModal, useBikeStore } from "hooks"

import FormElement from "components/Form/FormElement"

import { normalizeBoolean } from "utils/functions"
import { isNotEmpty } from "utils"
import dateService from "services/date"
import { BIKE_BACK_STORE, OCODE_GENDERS_LIST, bikeTypes, bikeBrands, getBrandLabelFromValue, getLabelFromBikeId, ADMIN_BICYCODE, REGISTER_MODE, UPDATE_MODE, BIKE_NOT_AVAILABLE, getIdFromBikeLabel } from "utils/constants"

import { OwnerDescription, BikeDescription } from "./Partials"

const defaultFormEvents = {
    ready: true
}

export const FormBikeInfos = (
    {
        readonly = false,
        saveMode = REGISTER_MODE,
        disableBikeInfos = false,
        toggleReadOnly = null
    }
) => {
    const {
        currentBike: bike,
        bikeStatus,
        setCurrentBike,
        setShowOwner: updateShowOwner,
    } = useBikeStore()

    const [formFields, setFormFields] = React.useState(bike)
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

    const [errors, setErrors] = React.useState([])
    const [showLocalErrors, setShowLocalErrors] = React.useState(false)

    const { alert } = useAlert()
    const { showModal } = useModal()
    const { t } = useTranslation()
    const { entity, user, logout } = useAuth()

    const isAdminOCode = OCODE_GENDERS_LIST.includes(user?.gender)
    const isOCodeEntity = OCODE_GENDERS_LIST.includes(entity?.gender)
    const isAdminBicyCode = user?.gender === ADMIN_BICYCODE
    const isViewedFromOCodeAdmin = isAdminOCode && !isOCodeEntity
    const isAdminUser = isAdminOCode || isAdminBicyCode // user that can modify bike description
    const isRegisterMode = saveMode === REGISTER_MODE
    const isUpdateMode = saveMode === UPDATE_MODE
    const isBikeNotAvailable = !bikeStatus || bikeStatus === BIKE_NOT_AVAILABLE

    const bikeIsDisabled = ((bike.status === BIKE_BACK_STORE) && (!isAdminOCode)) || disableBikeInfos

    // Bike has already a description
    const isBikePreRegistered = !bikeStatus && (bike.descriptionMadeBy === "STORE" || bike.descriptionMadeBy === "MANUFACTURER")

    const handleInputChange = (event) => {
        const target = event.target
        const value = target.type === "checkbox" ? target.checked : target.value
        const name = target.name

        setFormFields(s => ({
            ...s,
            [name]: value
        }))
    }

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
    }, [])

    const prepareBikesDatas = (bike) => {
        // Prepare datas to send to API
        const descriptionData = {
            gear_type_id: bike.bikeTypeId, // @TODO
            gear_type: bike.bikeType,
            brand_id: bike.bikeBrandId,
            brand: bike.bikeBrand,
            model: bike.bikeModel,
            color: bike.bikeColor,
            is_electric: normalizeBoolean(bike.isVAE),
            ...(isNotEmpty(bike.bikeSerial) ? { frame_serial_number: bike.bikeSerial } : {}),
            ...(bike.isVAE === "true" && isNotEmpty(bike.bikeEngineSerial) ? { engine_serial_number: bike.bikeEngineSerial } : {}),
            ...(bike.isVAE === "true" && isNotEmpty(bike.bikeBatterySerial) ? { battery_serial_number: bike.bikeBatterySerial } : {})
        }

        return {
            bike_id: bike.code,
            description_data: descriptionData,
            row_version: dateService.getCurrentDate("yyyyMMddHHmmssSSS")
        }
    }

    const prepareBikeDescription = (bike) => {
        // Prepare datas to send to API
        const bikeDescription = {
            gear_type_id: bike.bikeTypeId, // @TODO
            gear_type: bike.bikeType,
            brand_id: bike.bikeBrandId,
            brand: bike.bikeBrand,
            model: bike.bikeModel,
            color: bike.bikeColor,
            is_electric: normalizeBoolean(bike.isVAE),
            ...(isNotEmpty(bike.bikeSerial) ? { frame_serial_number: bike.bikeSerial } : {}),
            ...(bike.isVAE === "true" && isNotEmpty(bike.bikeEngineSerial) ? { engine_serial_number: bike.bikeEngineSerial } : {}),
            ...(bike.isVAE === "true" && isNotEmpty(bike.bikeBatterySerial) ? { battery_serial_number: bike.bikeBatterySerial } : {})
        }

        return {
            bike_id: bike.code,
            bike_description: bikeDescription,
            row_version: bike.row_version
        }
    }

    const handlePreRegistration = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            try {
                // hide error
                setShowLocalErrors(false)
                // prevent form to be submitted again
                setFormEvents({
                    ...formEvents,
                    ready: false
                })

                const newBike = prepareBikesDatas(formFields)

                const { getError } = await BikeApi.editBikeDescription(newBike)
                if (getError()) throw getError()
                alert("success", t("pre_registration.create_success", { ns: "bike", bikeId: newBike?.bike_id }))
                setFormEvents({
                    ...formEvents,
                    ready: true
                })

            } catch (error) {
                const erroMsg = error?.message?.toString()
                if (erroMsg) alert("error", erroMsg)

                if (["Access token is required."].includes(error?.error?.message)) logout()

            }
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents({
                ...formEvents,
                ready: false
            })

            showModal(
                ModalConfirmation,
                {
                    bike: formFields,
                    updateShowOwner,
                    callbackAndSell: () => {
                        // We update the current bike in the context with formfields
                        setCurrentBike((s) => ({
                            ...s,
                            ...formFields,
                            "bikeType": (formFields.bikeTypeId === 999) ? formFields.bikeType : t(`${getLabelFromBikeId(formFields.bikeTypeId)}`)
                        }))
                    }
                },
                { style: { maxWidth: "850px" } }

            )
            setFormEvents({
                ...formEvents,
                ready: true
            })
        }
    }

    const handleUpdate = async event => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)
        if (!formReady) {
            setShowLocalErrors(true)
        } else {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents({
                ...formEvents,
                ready: false
            })

            try {
                const updatedBike = prepareBikeDescription(formFields)
                const { getError, res } = await BikeApi.updateBikeDescription(updatedBike)
                if (getError()) throw getError()
                // Update BikeStore
                setCurrentBike((s) => ({
                    ...s,
                    ...formFields,
                    row_version: res.row_version
                }))
                // Update formFields
                setFormFields(s => {
                    const updated = s
                    updated.row_version = res.row_version
                    if (s.isVAE === "false") {
                        updated.bikeEngineSerial = ""
                        updated.bikeBatterySerial = ""
                    }
                    return updated
                })
                alert("success", t("edit_success", { ns: "bike" }))
            } catch (err) {
                alert("error", err.msg)
            }

            setFormEvents({
                ...formEvents,
                ready: true
            })
        }
    }

    /** Update form fields when bike is updated */
    React.useEffect(() => {
        setFormFields(bike)
        // Some bikes are no bikeTypeID specified. Try to find the bike type ID from the label or return 999
        if (!isNotEmpty(formFields.bikeTypeId)) {
            setFormFields(s => ({
                ...s,
                bikeTypeId: getIdFromBikeLabel(bike.bikeType) ?? 999
            }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bike])

    return (
        <>
            {readonly && (
                <div className="flex">
                    <BikeDescription className="flex-1" toggleReadOnly={!isBikeNotAvailable && isAdminUser ? toggleReadOnly : null} />
                    {isAdminOCode && !isViewedFromOCodeAdmin && (
                        <OwnerDescription className="flex-1 pl-7" />
                    )}
                </div>
            )}

            {!readonly &&
                <form className="c-card p-5 md:p-12 mb-5">
                    <section className="mb-5">
                        <h2 className="c-h2 font-semibold mb-5">{t("description_title", { ns: "bike" })}</h2>
                        <div className="l-grid u-mg-negative-hz-m">
                            <div className="lg:w-1/2 w-full p-5">
                                {/* bikeBrand */}
                                <FormElement
                                    value={formFields.bikeBrandId}
                                    defaultValue={{
                                        label: formFields.bikeBrandId ? t(`${getBrandLabelFromValue(formFields.bikeBrandId)}`) : "",
                                        value: formFields.bikeBrandId
                                    }}
                                    placeholder="test"
                                    options={bikeBrands}
                                    name="bikeBrandId"
                                    id="bikeBrandId"
                                    type="select"
                                    label={t("brand", { ns: "bike" })}
                                    className="lg:pr-12 mb-5"
                                    style={{ minWidth: "300px" }}
                                    required={true}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={event => {
                                        const value = event.target.value
                                        const label = event.target.label
                                        setFormFields({
                                            ...formFields,
                                            "bikeBrandId": value,
                                            "bikeBrand": (value === 999) ? "" : label
                                        })
                                    }}
                                    disabled={bikeIsDisabled}
                                />

                                { /* 999 = other brand */}
                                {(parseInt(formFields.bikeBrandId) === 999) &&
                                    <FormElement
                                        value={formFields.bikeBrand}
                                        name="bikeBrand"
                                        type="text"
                                        label={t("brand_new", { ns: "bike" })}
                                        className="lg:pr-12 mt-7 mb-5"
                                        style={{ minWidth: "300px" }}
                                        required={true}
                                        showErrors={showLocalErrors}
                                        onValidate={handleValidation}
                                        onChange={handleInputChange}
                                        disabled={bikeIsDisabled}
                                    />
                                }
                            </div>
                            <div className="lg:w-1/2 w-full p-5">
                                {/* bikeModel */}
                                <FormElement
                                    value={formFields.bikeModel}
                                    name="bikeModel"
                                    type="text"
                                    label={t("model", { ns: "bike" })}
                                    className="mb-5"
                                    required={true}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={handleInputChange}
                                    disabled={bikeIsDisabled}
                                />
                            </div>
                            <div className="lg:w-1/2 w-full p-5">
                                {/* bikeType */}
                                <FormElement
                                    value={formFields.bikeTypeId}
                                    defaultValue={{
                                        label: t(`${getLabelFromBikeId(formFields.bikeTypeId)}`),
                                        value: formFields.bikeTypeId
                                    }}
                                    options={bikeTypes.map((type) => ({ ...type, label: t(type.label) }))}
                                    name="bikeType"
                                    id="bikeType"
                                    type="select"
                                    label={t("type", { ns: "bike" })}
                                    className="lg:pr-12 mb-5"
                                    style={{ minWidth: "300px" }}
                                    required={true}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={event => {
                                        const value = event.target.value
                                        const label = t(event.target.label)
                                        setFormFields({
                                            ...formFields,
                                            "bikeTypeId": value,
                                            "bikeType": (value === 999) ? "" : label
                                        })
                                    }}
                                    disabled={bikeIsDisabled}
                                />

                                { /* 999 = other type */}
                                {(parseInt(formFields.bikeTypeId) === 999) &&
                                    <FormElement
                                        value={formFields.bikeType}
                                        name="bikeType"
                                        type="text"
                                        label={t("type_new", { ns: "bike" })}
                                        className="lg:pr-12 mt-7 mb-5"
                                        style={{ minWidth: "300px" }}
                                        required={true}
                                        showErrors={showLocalErrors}
                                        onValidate={handleValidation}
                                        onChange={handleInputChange}
                                        disabled={bikeIsDisabled}
                                    />
                                }
                            </div>
                            <div className="lg:w-1/2 w-full p-5">
                                {/* bikeColor */}
                                <FormElement
                                    value={formFields.bikeColor}
                                    name="bikeColor"
                                    type="text"
                                    label={t("color", { ns: "bike" })}
                                    className="mb-5"
                                    required={true}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={handleInputChange}
                                    disabled={bikeIsDisabled}
                                />
                            </div>
                            <div className="l-col-8 p-5">
                                {/* VAE */}
                                <FormElement
                                    value={formFields.isVAE}
                                    name="isVAE"
                                    type="radio"
                                    label={t("label_vae", { ns: "bike" })}
                                    // options={genderList.slice(0,4)}
                                    options={[
                                        { id: "vae-true", value: "true", label: "bike:label_vae_true" },
                                        { id: "vae-false", value: "false", label: "bike:label_vae_false" }
                                    ]}
                                    className="mb-7"
                                    classNameOptionsContainer="flex-row w-full"
                                    classNameLabel="mb-5"
                                    classNameOption="flex-1 items-center"
                                    required={true}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={handleInputChange}
                                    disabled={bikeIsDisabled}
                                />
                            </div>
                        </div>
                    </section>

                    <section className="mb-5">
                        <h2 className="c-h2 font-semibold mb-5">{t("optional_infos_title", { ns: "bike" })}</h2>
                        <div className="l-grid u-mg-negative-hz-m">
                            <div className="lg:w-1/2 w-full p-5">
                                {/* bikeSerial */}
                                <FormElement
                                    value={formFields.bikeSerial}
                                    name="bikeSerial"
                                    type="text"
                                    label={t("serial", { ns: "bike" })}
                                    className=""
                                    required={false}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        {normalizeBoolean(formFields?.isVAE) &&
                            <div className="l-grid u-mg-negative-hz-m">
                                <div className="lg:w-1/2 w-full p-5">
                                    {/* Bike Engine Serial */}
                                    <FormElement
                                        value={formFields.bikeEngineSerial}
                                        name="bikeEngineSerial"
                                        type="text"
                                        label={t("serial_engine", { ns: "bike" })}
                                        className=""
                                        required={false}
                                        showErrors={showLocalErrors}
                                        onValidate={handleValidation}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="lg:w-1/2 w-full p-5">
                                    {/* Bike Battery Serial */}
                                    <FormElement
                                        value={formFields.bikeBatterySerial}
                                        name="bikeBatterySerial"
                                        type="text"
                                        label={t("serial_battery", { ns: "bike" })}
                                        className=""
                                        required={false}
                                        showErrors={showLocalErrors}
                                        onValidate={handleValidation}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        }
                    </section>

                    {// bike is null and manufacturer describe bike => example with MFC
                        !bikeStatus && (bike?.descriptionMadeBy === "MANUFACTURER") &&
                        <p className="text-danger mt-12">
                            {t("pre_registration.manufaturer_description_modification", { ns: "bike" })}
                        </p>
                    }

                    <div className={`flex ${isRegisterMode ? "justify-between" : "flex-row-reverse"} u-relative mt-12 mb-5`}>
                        {!formEvents.ready &&
                            <div className="c-spinner"></div>
                        }
                        {formEvents.ready &&
                            <>
                                {bikeStatus && isRegisterMode &&
                                    <button
                                        type="submit"
                                        className="c-btn c-btn--primary px-5 uppercase ml-auto"
                                        onClick={handleSubmit}
                                    >
                                        {t("global.save")}
                                    </button>
                                }
                                {bikeStatus && isAdminUser && isUpdateMode &&
                                    <>
                                        <button
                                            type="submit"
                                            className="c-btn c-btn--primary px-5 ml-2"
                                            onClick={handleUpdate}
                                        >
                                            {t("global.update")}
                                        </button>
                                        {toggleReadOnly &&
                                            <button
                                                className="c-btn px-5"
                                                onClick={toggleReadOnly}
                                            >
                                                {t("global.cancel")}
                                            </button>
                                        }
                                    </>
                                }
                                {!bikeStatus &&
                                    <>
                                        <div>
                                            <p className="uppercase mb-5">
                                                {bike?.descriptionMadeBy === "MANUFACTURER"
                                                    ? t("pre_registration.manufacturer_describe", { ns: "bike" })
                                                    : bike?.descriptionMadeBy === "STORE"
                                                        ? t("pre_registration.store_describe", { ns: "bike" })
                                                        : t("pre_registration.register_description", { ns: "bike" })
                                                }
                                            </p>
                                            <button
                                                type="submit"
                                                className={"c-btn px-5 uppercase " + (isBikePreRegistered ? "c-btn--secondary" : "c-btn--primary")}
                                                onClick={handlePreRegistration}
                                            >
                                                {isBikePreRegistered ? t("global.update") : t("pre_registration.pre_registration", { ns: "bike" })}
                                            </button>
                                        </div>
                                        <div>
                                            <p className="uppercase mb-5">{t("pre_registration.add_owner", { ns: "bike" })}</p>
                                            <button
                                                type="submit"
                                                className="c-btn c-btn--primary px-5 uppercase ml-auto"
                                                onClick={handleSubmit}
                                            >
                                                {t("global.save")}
                                            </button>
                                        </div>
                                    </>
                                }

                            </>
                        }
                    </div>
                </form>
            }
        </>
    )

}





const ModalConfirmation = ({ bike, callbackAndSell, updateShowOwner }) => {
    const { hideModal } = useModal()
    const { t } = useTranslation()

    const validateAndSell = (response) => {
        hideModal()
        callbackAndSell(response)
        if (updateShowOwner) updateShowOwner(true)
    }

    return (
        <div className="p-12">
            <h2 className="c-h2 text-primary text-xl font-bold text-center my-7">{t("create_modal_confirm_title", { ns: "bike" })}</h2>
            <p className="">{t("create_modal_confirm_description_1", { ns: "bike" })} <strong>{t("create_modal_confirm_description_2", { ns: "bike" })}</strong></p>

            <p className="mt-7 mb-2">{t("create_modal_confirm_description_3", { ns: "bike" })}</p>
            <p className="font-bold text-m">
                <strong>{bike?.bikeCode || bike?.code}</strong><br />
                {t(bike?.bikeType)} &ndash; {bike?.bikeBrand} &ndash; {bike?.bikeModel} &ndash; {bike?.bikeColor} <br />
                {normalizeBoolean(bike?.isVAE) ? t("type_electric", { ns: "bike" }) : t("type_mecanical", { ns: "bike" })}
            </p>
            <div className="flex justify-between flex-col md:flex-row-reverse mt-7">
                <button
                    type="button"
                    className="c-btn c-btn--primary px-7 mb-5 lg:m-0 uppercase"
                    onClick={validateAndSell}
                >
                    {t("global.confirm")}
                </button>

                <button
                    type="button"
                    className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0 uppercase"
                    onClick={hideModal}
                >
                    {t("global.cancel")}
                </button>
            </div>
        </div>
    )
}
